import React, { FC, useCallback, useState } from 'react'
import { Typography } from 'react-components'
import { CTAButton, StyledGhostButton } from '../style'
import { FormattedBookingTypeData } from '../../../models/property-details-page/booking-type-model'
import { SearchbarFieldsReturn, UpdateSearchBarTransport } from '../hooks/use-searchbar'
import get from 'lodash/get'
import getChildrenAges from '../utils/get-children-ages'
import useDynamicQueryParams from '../hooks/use-dynamic-query-params'
import { pdpPageActions } from '../../../services/analytics-service/actions'
import { pdpPageCategories } from '../../../services/analytics-service/categories'
import AnalyticsService from '../../../services/analytics-service'
import { useQuery } from '../../../hooks/query'
import { useTranslation } from '../../../hooks/locale'
import InquiryContactDetailsTransport from '../../../transports/inquiry-contact-details-transport'
import { FormattedLivePricingDataTransport } from '../../../models/pricing/live-pricing-formatter'
import { CallPickerIcon } from '../../search-result-listing/header-section/searchbar/widget-styles'
import { ShareIconWrapper } from '../book-now-button/styles'
import UserDetailsModel from '../../../models/user/user-details-model'
import { PdpToCheckoutPosition } from '../constants'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY } from '../../../common/constants'

type CallButtonProps = {
    bookingType: FormattedBookingTypeData | null
    searchbar: SearchbarFieldsReturn
    position:
        | PdpToCheckoutPosition.GALLERY
        | PdpToCheckoutPosition.SIDEBAR
        | PdpToCheckoutPosition.STICKY_FOOTER
        | PdpToCheckoutPosition.PRICE_WIDGET
    inquiryContactDetails: InquiryContactDetailsTransport | null
    pricingDetails: FormattedLivePricingDataTransport | null
    isPricingLoading: boolean
    isAvailabilityLoading: boolean
    pricingErrorCode: number | null
    updateSearchbar: UpdateSearchBarTransport
    availableDates: Date[] | null
    userDetails: UserDetailsModel | null
    petsAllowed: boolean
}

const CallButton: FC<CallButtonProps> = props => {
    const { bookingType, searchbar, position, ...restProps } = props

    const query = useQuery()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const dynamicQueryParams = JSON.parse(get(bookingType, 'subType.dynamicQueryParams', '{}'))
    const childrenAges = getChildrenAges(searchbar.kids)
    const qpValues = { ...query, ...searchbar, childrenAges }
    const url = useDynamicQueryParams(bookingType?.subType, qpValues, dynamicQueryParams)
    const type = get(bookingType, 'subType.type')
    const { t } = useTranslation()

    const [isEnquiryPopupOpen, setEnquiryPopupVisibility] = useState(false)
    const [EnquiryPopup, setEnquiryPopup] = useState<React.ComponentType<any> | null>(null)

    const { isProvidedVariantActive, isLoading: abLoading, error } = useABVariant(AB_KEY.CHECKOUT_MERGER, 1)

    const showEnquiryPopup = useCallback(async () => {
        if (!EnquiryPopup) {
            setIsLoading(true)
            const module = await import('../enquiry-button/enquiry-popup')
            setEnquiryPopup(() => module.default)
            setIsLoading(false)
        }
        setEnquiryPopupVisibility(true)
    }, [EnquiryPopup])

    const hideEnquiryPopup = useCallback(() => setEnquiryPopupVisibility(false), [])

    const onCallClick = useCallback(
        (event: React.MouseEvent) => {
            setTimeout(() => {
                AnalyticsService.trackEvent({
                    category: pdpPageCategories.PDP_PAGE,
                    action: pdpPageActions.CALL_BUTTON_CLICKED,
                    label: position,
                })
            }, 0)

            if (type === 'redirect') {
                event.preventDefault()
                setIsLoading(true)
                window.open(url, '_blank')
                setIsLoading(false)
            } else {
                if (isProvidedVariantActive) {
                    event.preventDefault()
                    showEnquiryPopup()
                } else {
                    setIsLoading(true)
                    setTimeout(() => {
                        setIsLoading(false)
                    }, 5000)
                }
            }
        },
        [type, url, position, showEnquiryPopup, isProvidedVariantActive],
    )

    return (
        <>
            <CTAButton>
                <StyledGhostButton
                    isPrimary={false}
                    href={url}
                    onClick={onCallClick}
                    disabled={isLoading || abLoading ? true : undefined}
                    loading={isLoading || abLoading ? true : undefined}
                    buttonSize="large"
                >
                    <ShareIconWrapper>
                        <CallPickerIcon alt={t('callIconText')} style={{ position: 'unset' }} />
                    </ShareIconWrapper>
                    <Typography variant="subtitle2" fontWeight="semiBold">
                        {t('call')}
                    </Typography>
                </StyledGhostButton>
            </CTAButton>
            {!!EnquiryPopup && (
                <EnquiryPopup
                    isOpen={isEnquiryPopupOpen}
                    onClose={hideEnquiryPopup}
                    searchbar={searchbar}
                    {...restProps}
                />
            )}
        </>
    )
}

export default CallButton
